import { FaberApi, JetpacSimRevampOpsApi } from 'api'
import { AxiosError, AxiosPromise } from 'axios'
import OrbitSession from 'hooks/useCookies'
import {
  EsimsInfo,
  EsimsInfoPaginated,
  EsimStatusType
} from 'types/api/faber/dataUsage.type'
import {
  AllocateEsimRequest,
  AllocateEsimResponse,
  PackAction,
  PackAllocationsResponse,
  PackAssignmentResponse,
  ReplaceEsimRequest,
  ReplaceEsimResponse,
  ServiceCatalogItemsResponse,
  ServiceItemType,
  TerminateEsimResponse
} from 'types/api/ops/v2/simOps.type'

const getAuthHeaders = () => {
  const faberToken = OrbitSession.get('faberToken')
  return { Authorization: `Bearer ${faberToken}` }
}

// sim ops errors come back as { status, message?, error? }
export const getSimOpsErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    return (
      error.response?.data?.error || error.response?.data?.message || fallback
    )
  }
  return fallback
}

class SimOpsAPI {
  static getUserEsims({
    userId,
    limit,
    offset
  }: {
    userId: string
    limit: number
    offset: number
  }): AxiosPromise<EsimsInfoPaginated> {
    const params = new URLSearchParams({
      view: 'list',
      limit: String(limit),
      offset: String(offset),
      excludeStatus: 'TERMINATED'
    })
    return JetpacSimRevampOpsApi.httpGet(
      `/esims/user/${userId}?${params.toString()}`,
      getAuthHeaders()
    )
  }

  static getUserEsimSummary(userId: string): AxiosPromise<EsimsInfo> {
    return JetpacSimRevampOpsApi.httpGet(
      `/esims/user/${userId}?view=summary`,
      getAuthHeaders()
    )
  }

  static getUserEsimLiveStatus(userId: string): AxiosPromise<EsimStatusType> {
    return JetpacSimRevampOpsApi.httpGet(
      `/esims/user/${userId}/live-status`,
      getAuthHeaders()
    )
  }

  static getUserAllocations({
    userId,
    limit,
    offset
  }: {
    userId: string
    limit: number
    offset: number
  }): AxiosPromise<PackAllocationsResponse> {
    return JetpacSimRevampOpsApi.httpGet(
      `/allocations/user/${userId}?limit=${limit}&offset=${offset}`,
      getAuthHeaders()
    )
  }

  // lounge pass / fast track allocations, kept out of getUserAllocations
  static getUserServiceCatalogItems({
    userId,
    limit,
    offset,
    types
  }: {
    userId: string
    limit: number
    offset: number
    types?: ServiceItemType[]
  }): AxiosPromise<ServiceCatalogItemsResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset)
    })
    if (types?.length) params.append('type', types.join(','))

    return JetpacSimRevampOpsApi.httpGet(
      `/service-catalog-items/user/${userId}?${params.toString()}`,
      getAuthHeaders()
    )
  }

  static allocateEsim(
    body: AllocateEsimRequest
  ): AxiosPromise<AllocateEsimResponse> {
    return JetpacSimRevampOpsApi.httpPost(
      '/esim/allocate',
      getAuthHeaders(),
      body
    )
  }

  static replaceEsim(
    body: ReplaceEsimRequest
  ): AxiosPromise<ReplaceEsimResponse> {
    return JetpacSimRevampOpsApi.httpPost(
      '/esim/replace',
      getAuthHeaders(),
      body
    )
  }

  static terminateEsim(iccid: string): AxiosPromise<TerminateEsimResponse> {
    return JetpacSimRevampOpsApi.httpPost(
      `/esim/${encodeURIComponent(iccid)}/terminate`,
      getAuthHeaders(),
      {}
    )
  }

  // moves an INACTIVE pack to another receiver; omitting receiverEmail
  // reallocates it back to its owner. Lives outside the /ops prefix.
  static reassignPack(body: {
    packAssignmentId: string
    receiverEmail?: string
    receiverId?: string
  }): AxiosPromise<PackAssignmentResponse> {
    return FaberApi.httpPost(
      '/sim/revamp/pack-assignment/reassign',
      getAuthHeaders(),
      body
    )
  }

  // replay: stuck/failed pack with no active plan yet
  // terminate: terminate the plan and move the pack back to PENDING
  // reset: terminate + replay in one call
  static packAction({
    packAssignmentId,
    action
  }: {
    packAssignmentId: string
    action: PackAction
  }): AxiosPromise<PackAssignmentResponse> {
    return JetpacSimRevampOpsApi.httpPost(
      `/pack/${packAssignmentId}/${action}`,
      getAuthHeaders(),
      {}
    )
  }

  // re-syncs an ACTIVE/INACTIVE pack's status and usage from the vendor
  static refreshPack(
    packAssignmentId: string
  ): AxiosPromise<{ status: string; message?: string }> {
    return JetpacSimRevampOpsApi.httpPost(
      `/pack/${packAssignmentId}/refresh`,
      getAuthHeaders(),
      {}
    )
  }
}

export default SimOpsAPI
